import React, { useMemo, useState } from 'react';
import { Empty, Spin, Table, Tag, Typography, Tooltip, DatePicker } from 'antd';
import { InfoCircleOutlined, AlertOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import {
  useHighPvRealizationBacktest,
  type BacktestDay,
} from '@/api/weatherHighPvRealization';

const { Text } = Typography;

const GRADE_LABEL: Record<string, string> = {
  fully_realized: '完全兑现',
  partially_realized: '部分兑现',
  weak_realized: '弱兑现',
  failed_realized: '未兑现',
};

const GRADE_COLOR: Record<string, string> = {
  fully_realized: 'green',
  partially_realized: 'blue',
  weak_realized: 'orange',
  failed_realized: 'red',
};

const CAUSE_LABEL: Record<string, string> = {
  normal: '正常',
  unknown: '未知',
  curtailment_suspected_high: '疑似限电(高)',
  curtailment_suspected_medium: '疑似限电(中)',
  weather_likely: '天气因素',
  mixed: '多重因素',
};

const CAUSE_COLOR: Record<string, string> = {
  normal: 'default',
  unknown: 'default',
  curtailment_suspected_high: 'volcano',
  curtailment_suspected_medium: 'orange',
  weather_likely: 'blue',
  mixed: 'purple',
};

function fmtPct(val: number | null | undefined): string {
  if (val === null || val === undefined) return '-';
  return `${(val * 100).toFixed(1)}%`;
}

function fmtMw(val: number | null | undefined): string {
  if (val === null || val === undefined) return '-';
  return `${val.toFixed(0)} MW`;
}

const COLUMNS = [
  { title: '日期', dataIndex: 'date', key: 'date', width: 100 },
  { title: '官方MW', dataIndex: 'official_midday_mw', key: 'official', width: 80, align: 'right' as const },
  { title: '实际MW', dataIndex: 'actual_midday_mw', key: 'actual', width: 80, align: 'right' as const },
  { title: '兑现率', dataIndex: 'realization_ratio', key: 'ratio', width: 80, align: 'right' as const },
  { title: '兑现等级', dataIndex: 'realization_grade', key: 'grade', width: 90 },
  { title: '归因', dataIndex: 'cause_label', key: 'cause', width: 110 },
  { title: '缺口MW', dataIndex: 'shortfall_mw', key: 'shortfall', width: 70, align: 'right' as const },
];

const HighPvRealizationBacktestPanel: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(60, 'day'),
    dayjs().subtract(1, 'day'),
  ]);

  const startDate = dateRange[0].format('YYYY-MM-DD');
  const endDate = dateRange[1].format('YYYY-MM-DD');

  const { data, isLoading, error } = useHighPvRealizationBacktest(startDate, endDate);

  const recentHighPvDays = useMemo(() => {
    if (!data?.days) return [];
    return data.days
      .filter((d) => d.is_high_pv)
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 20);
  }, [data]);

  if (isLoading) {
    return (
      <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, marginBottom: 14 }}>
        <Spin />
      </section>
    );
  }

  if (error) {
    return (
      <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, marginBottom: 14 }}>
        <Empty description="高光伏兑现回测暂不可用" />
      </section>
    );
  }

  if (!data?.summary || data.summary.high_pv_days === 0) {
    return (
      <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, marginBottom: 14 }}>
        <Empty description="所选时段暂无高光伏日样本" />
      </section>
    );
  }

  const { summary, buckets, data_quality } = data;
  const appliedBucket = buckets.find((b) => b.applied);
  const recommendedDiscount = appliedBucket?.recommended_discount_ratio ?? summary.avg_realization_ratio;
  const riskDiscount = appliedBucket?.risk_discount_ratio ?? null;

  return (
    <section style={{
      border: '1px solid #e8d9a0',
      borderRadius: 8,
      background: '#fefdf7',
      padding: 16,
      marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <AlertOutlined style={{ color: '#d48806' }} />
        <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
          高光伏兑现回测
        </div>
        <Tooltip title="回测官方集中式光伏高预测日的历史兑现情况，为交易判断提供统计佐证，不构成明日结果保证。">
          <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
        </Tooltip>
        <div style={{ flex: 1 }} />
        <DatePicker.RangePicker
          size="small"
          value={dateRange}
          onChange={(v) => { if (v && v[0] && v[1]) setDateRange([v[0], v[1]]); }}
          allowClear={false}
          disabledDate={(d) => d.isAfter(dayjs().subtract(1, 'day'), 'day')}
          style={{ width: 240 }}
        />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 8,
        marginBottom: 10,
      }}>
        <KpiBox label="高光伏日样本" value={summary.high_pv_days} unit="天" />
        <KpiBox label="平均兑现率" value={fmtPct(summary.avg_realization_ratio)} />
        <KpiBox label="弱/未兑现占比" value={fmtPct(summary.weak_or_failed_rate)} color={summary.weak_or_failed_rate && summary.weak_or_failed_rate > 0.3 ? '#a8071a' : undefined} />
        <KpiBox label="平均缺口" value={fmtMw(summary.avg_shortfall_mw)} />
        <KpiBox label="P80 缺口" value={fmtMw(summary.p80_shortfall_mw)} />
        <KpiBox label="天气提前捕捉率" value={fmtPct(summary.weather_adjusted_capture)}
          hint="弱/未兑现日中，模型辐照低于官方预报的比例：衡量模型是否提前预示了出力不足" />
        <KpiBox label="推荐折扣" value={fmtPct(recommendedDiscount)}
          hint="基于相似历史样本中位数兑现率，用于交易申报保守修正" />
        {riskDiscount !== null && (
          <KpiBox label="风险折扣" value={fmtPct(riskDiscount)}
            hint="基于相似历史样本 P20 兑现率（80% 置信下限），用于极端风险情景" />
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <Tag color="green">完全兑现 {fmtPct(summary.full_realization_rate)}</Tag>
        <Tag color="red">未兑现 {fmtPct(summary.failed_rate)}</Tag>
        {data_quality?.incomplete_days > 0 && (
          <Tooltip title={`${data_quality.incomplete_days} 天数据不完整已排除`}>
            <Tag color="default">数据排除 {data_quality.incomplete_days}d</Tag>
          </Tooltip>
        )}
      </div>

      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
        回测官方集中式光伏预测（forecast）与实际上网出力（reality）在午间 10-14 时的兑现关系。
        高光伏日指官方预测午间平均出力 &ge; 集中式装机 50% 的交易日。以下显示最近高光伏日样本，可按日期回溯验证。
      </Text>

      <Table<BacktestDay>
        dataSource={recentHighPvDays}
        rowKey="date"
        size="small"
        pagination={false}
        scroll={{ x: 720 }}
        style={{ fontSize: 12 }}
        columns={COLUMNS.map((col) => {
          if (col.key === 'date') {
            return { ...col, render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text> };
          }
          if (col.key === 'official' || col.key === 'actual') {
            return { ...col, render: (v: number) => <Text style={{ fontSize: 12 }}>{v.toFixed(0)}</Text> };
          }
          if (col.key === 'ratio') {
            return { ...col, render: (v: number | null) => (
              <Text style={{ fontSize: 12, fontWeight: 600, color: v !== null && v >= 0.9 ? '#237804' : v !== null && v < 0.8 ? '#a8071a' : '#ad6800' }}>
                {fmtPct(v)}
              </Text>
            )};
          }
          if (col.key === 'grade') {
            return { ...col, render: (v: string) => <Tag color={GRADE_COLOR[v]} style={{ fontSize: 11, margin: 0 }}>{GRADE_LABEL[v] || v}</Tag> };
          }
          if (col.key === 'cause') {
            return { ...col, render: (v: string) => <Tag color={CAUSE_COLOR[v]} style={{ fontSize: 11, margin: 0 }}>{CAUSE_LABEL[v] || v}</Tag> };
          }
          if (col.key === 'shortfall') {
            return { ...col, render: (v: number) => (
              <Text style={{ fontSize: 12, color: v > 0 ? '#a8071a' : '#237804' }}>
                {v > 0 ? `-${v.toFixed(0)}` : `+${Math.abs(v).toFixed(0)}`}
              </Text>
            )};
          }
          return col;
        })}
      />
    </section>
  );
};

function KpiBox({
  label,
  value,
  unit,
  color,
  hint,
}: {
  label: string;
  value: string | number;
  unit?: string;
  color?: string;
  hint?: string;
}) {
  const content = (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: 6,
      background: '#fff',
      padding: '8px 12px',
      textAlign: 'center',
    }}>
      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{label}</Text>
      <div style={{ fontSize: 18, fontWeight: 700, color: color ?? '#1f2937', marginTop: 2 }}>
        {value}
        {unit && <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 2 }}>{unit}</span>}
      </div>
    </div>
  );
  if (hint) return <Tooltip title={hint}>{content}</Tooltip>;
  return content;
}

export default HighPvRealizationBacktestPanel;
